import { useTranslations } from '@aikoa/i18n/react'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef } from 'react'
import { View } from 'react-native'
import Icon from '~/components/Icon'
import Image from '~/components/layout/Image'
import { Heading3 } from '~/components/typography/Titles'
import { Skeleton } from '~/components/ui/skeleton'
import { Text } from '~/components/ui/text'
import { trpc } from '~/lib/trpc'
import { cn, isWebUrl } from '~/lib/utils'

export function UrlPreview({
  url,
  onLoad,
  onError,
  className,
}: {
  url: string
  onLoad?: () => void
  onError?: (error: 'INVALID_URL' | 'NO_IMAGE' | 'URL_ERROR') => void
  className?: string
}) {
  const { t } = useTranslations('common')
  const isValid = useMemo(() => isWebUrl(url), [url])
  const baseOptions = trpc.preview.getSocialPreview.queryOptions({ url })

  const {
    data: preview,
    isLoading,
    isError,
  } = useQuery({
    ...baseOptions,
    enabled: isValid,
  })

  const imageUrl = preview?.images?.[0]?.url

  const host = useMemo(() => {
    try {
      return new URL(url).hostname
    } catch {
      return ''
    }
  }, [url])

  const derivedError: 'INVALID_URL' | 'NO_IMAGE' | 'URL_ERROR' | undefined = useMemo(
    () =>
      !isValid
        ? 'INVALID_URL'
        : isError || !preview
          ? 'URL_ERROR'
          : !imageUrl
            ? 'NO_IMAGE'
            : undefined,
    [isValid, isError, preview, imageUrl]
  )

  const notifiedError = useRef<string | undefined>(undefined)
  const notifiedLoadedWithoutImage = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (isLoading) {
      return
    }

    if (derivedError && onError && derivedError !== notifiedError.current) {
      notifiedError.current = derivedError
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
      onError(derivedError)
    }

    if (!derivedError) {
      notifiedError.current = undefined
    }
  }, [derivedError, onError, isLoading])

  useEffect(() => {
    if (isLoading) {
      return
    }

    if (isValid && !isError && preview && !imageUrl) {
      if (notifiedLoadedWithoutImage.current !== url) {
        notifiedLoadedWithoutImage.current = url
        onLoad?.()
      }
    }
  }, [isLoading, isValid, isError, preview, imageUrl, url, onLoad])

  const content = (
    <View className={cn('relative aspect-video w-full overflow-hidden rounded-xl', className)}>
      {imageUrl ? (
        <Image
          source={{ uri: imageUrl }}
          contentFit="cover"
          cachePolicy="memory-disk"
          transition={0}
          onLoad={onLoad}
          style={{ width: '100%', height: '100%' }}
        />
      ) : (
        <View className="flex-1 items-start justify-center bg-muted p-4">
          <Icon name="image-off-outline" size={42} className="text-secondary" />
        </View>
      )}

      <View className="pointer-events-none absolute inset-x-0 bottom-0 h-full bg-linear-to-t from-primary/90 to-transparent" />

      <View className="pointer-events-none absolute bottom-0 left-0 right-0 gap-2 p-4">
        <Text
          className="text-xs text-background opacity-90 shadow-black drop-shadow-sm"
          numberOfLines={1}>
          {host}
        </Text>
        {!!preview?.title && (
          <Heading3
            className="mt-0.5 font-medium text-xl/snug text-background shadow-black drop-shadow-sm"
            numberOfLines={2}>
            {preview.title}
          </Heading3>
        )}
      </View>
    </View>
  )

  if (isLoading) {
    return <Skeleton className="bg-muted/60">{content}</Skeleton>
  }

  if (isError || !preview) {
    return (
      <View
        className="w-full overflow-hidden rounded-xl border border-border bg-muted/50"
        style={{ aspectRatio: 16 / 9 }}>
        <View className="flex-1 items-start justify-end p-4">
          <Heading3 className="mb-1 text-foreground" numberOfLines={2}>
            {t(($) => $.urlPreview.notAvailable)}
          </Heading3>
          <Text className="text-xs text-secondary" numberOfLines={1}>
            {host}
          </Text>
        </View>
      </View>
    )
  }

  return content
}
